import { Point } from 'pixi.js';
import { GridSystem } from '@gameplay/building/GridSystem';
import { WallSystem } from '@gameplay/building/WallSystem';
import { DoorSystem } from '@gameplay/building/DoorSystem';
import { PlacementSystem } from '@gameplay/building/PlacementSystem';
import { FurnitureRotation } from '@gameplay/building/FurnitureSystem';
import { RoomManager } from '@gameplay/management/rooms/RoomManager';
import { ResidentManager } from '@gameplay/simulation/ResidentManager';
import { RoomCategory } from '@gameplay/management/rooms/RoomTypes';

export interface DebugScenario {
  name: string;
  description: string;
  size: { width: number; height: number };
  rooms: DebugRoom[];
  furniture: DebugFurniture[];
  residentSpawns: Point[];
}

export interface DebugRoom {
  category: RoomCategory;
  bounds: { x: number; y: number; width: number; height: number };
  doorways?: Point[]; // Optional specific door positions
}

export interface DebugFurniture {
  type: string;
  position: Point;
  rotation?: FurnitureRotation;
}

export class DebugBuilder {
  private readonly gridSystem: GridSystem;
  private readonly wallSystem: WallSystem;
  private readonly doorSystem: DoorSystem;
  private readonly placementSystem: PlacementSystem;
  private readonly roomManager: RoomManager;
  private readonly residentManager: ResidentManager;

  constructor(
    gridSystem: GridSystem,
    wallSystem: WallSystem,
    doorSystem: DoorSystem,
    placementSystem: PlacementSystem,
    roomManager: RoomManager,
    residentManager: ResidentManager
  ) {
    this.gridSystem = gridSystem;
    this.wallSystem = wallSystem;
    this.doorSystem = doorSystem;
    this.placementSystem = placementSystem;
    this.roomManager = roomManager;
    this.residentManager = residentManager;
  }

  public clearAll(): void {
    console.log('🧹 Clearing all existing structures...');
    
    // Clear all grid objects
    const allObjects = this.gridSystem.getAllObjects();
    for (const obj of allObjects) {
      this.gridSystem.removeObject(obj.gridX, obj.gridY, obj.floor);
    }
    
    // Clear all walls
    this.wallSystem.clear();
    
    // Clear all doors
    this.doorSystem.clear();
    
    // Clear all rooms
    this.roomManager.clear();
    
    // Clear all residents
    const residents = this.residentManager.getAllResidents();
    for (const resident of residents) {
      this.residentManager.removeResident(resident.stats.id);
    }
  }

  public buildScenario(scenario: DebugScenario): void {
    console.log(`🏗️ Building debug scenario: ${scenario.name}`);
    
    this.clearAll();
    
    // Build floors for each room
    for (const room of scenario.rooms) {
      this.buildRoomFloors(room);
    }
    
    // Build walls around rooms (leaving doorways)
    for (const room of scenario.rooms) {
      this.buildRoomWalls(room);
    }
    
    // Place doors at doorway positions
    for (const room of scenario.rooms) {
      this.placeDoors(room);
    }
    
    // Set room categories
    for (const room of scenario.rooms) {
      this.setRoomCategory(room);
    }
    
    // Place furniture
    for (const furniture of scenario.furniture) {
      this.placeFurniture(furniture);
    }
    
    // Spawn residents
    for (const spawnPoint of scenario.residentSpawns) {
      this.residentManager.addResident(spawnPoint);
    }
    
    console.log(`✅ Debug scenario '${scenario.name}' built successfully!`);
  }

  private buildRoomFloors(room: DebugRoom): void {
    for (let x = room.bounds.x; x < room.bounds.x + room.bounds.width; x++) {
      for (let y = room.bounds.y; y < room.bounds.y + room.bounds.height; y++) {
        this.placementSystem.placeFloor(new Point(x, y), 0);
      }
    }
  }

  private buildRoomWalls(room: DebugRoom): void {
    const bounds = room.bounds;
    
    // Build walls using a single perimeter drag operation
    this.placementSystem.setMode('wall');
    
    // Create one continuous wall around the entire room perimeter
    // Start from top-left corner and drag to bottom-right corner to encompass the room
    const startPoint = new Point(bounds.x, bounds.y);
    const endPoint = new Point(bounds.x + bounds.width - 1, bounds.y + bounds.height - 1);
    
    // Simulate dragging the entire room area to create walls around the perimeter
    this.simulateWallDrag(startPoint, endPoint);
    
    // Now remove wall segments where doors should be placed
    this.removeWallsForDoorways(room);
  }

  private removeWallsForDoorways(room: DebugRoom): void {
    if (!room.doorways || room.doorways.length === 0) return;
    
    const bounds = room.bounds;
    
    for (const doorway of room.doorways) {
      // Determine which wall this doorway is on and remove that wall segment
      let wallStartX, wallStartY, wallEndX, wallEndY;
      
      if (Math.abs(doorway.y - (bounds.y - 0.5)) < 0.1) {
        // Top wall
        wallStartX = Math.floor(doorway.x);
        wallStartY = bounds.y - 0.5;
        wallEndX = Math.floor(doorway.x) + 1;
        wallEndY = bounds.y - 0.5;
      } else if (Math.abs(doorway.y - (bounds.y + bounds.height - 0.5)) < 0.1) {
        // Bottom wall
        wallStartX = Math.floor(doorway.x);
        wallStartY = bounds.y + bounds.height - 0.5;
        wallEndX = Math.floor(doorway.x) + 1;
        wallEndY = bounds.y + bounds.height - 0.5;
      } else if (Math.abs(doorway.x - (bounds.x - 0.5)) < 0.1) {
        // Left wall
        wallStartX = bounds.x - 0.5;
        wallStartY = Math.floor(doorway.y);
        wallEndX = bounds.x - 0.5;
        wallEndY = Math.floor(doorway.y) + 1;
      } else if (Math.abs(doorway.x - (bounds.x + bounds.width - 0.5)) < 0.1) {
        // Right wall
        wallStartX = bounds.x + bounds.width - 0.5;
        wallStartY = Math.floor(doorway.y);
        wallEndX = bounds.x + bounds.width - 0.5;
        wallEndY = Math.floor(doorway.y) + 1;
      } else {
        continue;
      }
      
      // Remove the wall segment where the door should be
      this.wallSystem.removeSegment(wallStartX, wallStartY, wallEndX, wallEndY, 0);
    }
  }

  private simulateWallDrag(start: Point, end: Point): void {
    this.placementSystem.beginDrag(start);
    this.placementSystem.updateDrag(end);
    this.placementSystem.endDrag(end);
  }

  private setRoomCategory(room: DebugRoom): void {
    // Set category for all tiles in the room
    for (let x = room.bounds.x; x < room.bounds.x + room.bounds.width; x++) {
      for (let y = room.bounds.y; y < room.bounds.y + room.bounds.height; y++) {
        this.roomManager.setTileCategory(x, y, 0, room.category);
      }
    }
  }

  private placeFurniture(furniture: DebugFurniture): void {
    // Set furniture mode and select the furniture type
    this.placementSystem.setMode('furniture');
    this.placementSystem.setSelectedFurniture(furniture.type);
    this.placementSystem.setFurnitureRotation(furniture.rotation || 0);
    
    const result = this.placementSystem.placeFurnitureAt(furniture.position, 0);
    if (!result) {
      console.warn(`⚠️ Failed to place ${furniture.type} at (${furniture.position.x}, ${furniture.position.y})`);
    }
  }

  // Predefined test scenarios
  public static getBasicTestScenario(): DebugScenario {
    return {
      name: "Basic Test Layout",
      description: "Simple layout with all room types for testing resident behavior",
      size: { width: 20, height: 15 },
      rooms: [
        {
          category: 'bedroom',
          bounds: { x: 2, y: 2, width: 4, height: 3 },
          doorways: [new Point(6, 3.5)] // Door on the right
        },
        {
          category: 'bathroom',
          bounds: { x: 2, y: 6, width: 3, height: 2 },
          doorways: [new Point(5, 7.5)] // Door on the right
        },
        {
          category: 'dining',
          bounds: { x: 8, y: 2, width: 5, height: 4 },
          doorways: [new Point(7.5, 4)] // Door on the left
        },
        {
          category: 'medical',
          bounds: { x: 8, y: 7, width: 4, height: 3 },
          doorways: [new Point(7.5, 8)] // Door on the left
        },
        {
          category: 'recreation',
          bounds: { x: 14, y: 2, width: 4, height: 6 },
          doorways: [new Point(13.5, 5)] // Door on the left
        },
        {
          category: 'utility',
          bounds: { x: 6, y: 2, width: 2, height: 8 },
          doorways: [] // Open hallway connections
        }
      ],
      furniture: [
        // Bedroom furniture
        { type: 'bed_twin', position: new Point(3, 3), rotation: 0 },
        { type: 'dresser', position: new Point(5, 2), rotation: 0 },
        
        // Bathroom furniture
        { type: 'toilet', position: new Point(3, 6), rotation: 0 },
        { type: 'sink', position: new Point(4, 7), rotation: 0 },
        
        // Dining furniture
        { type: 'dining_table', position: new Point(10, 4), rotation: 0 },
        { type: 'chair', position: new Point(9, 4), rotation: 0 },
        { type: 'chair', position: new Point(11, 4), rotation: 180 },
        
        // Medical furniture
        { type: 'hospital_bed', position: new Point(9, 8), rotation: 0 },
        { type: 'medical_cabinet', position: new Point(11, 7), rotation: 0 },
        
        // Recreation furniture
        { type: 'sofa', position: new Point(15, 4), rotation: 0 },
        { type: 'coffee_table', position: new Point(16, 5), rotation: 0 }
      ],
      residentSpawns: [
        new Point(100, 100), // Hallway spawn
        new Point(120, 120), // Near bedroom
        new Point(140, 140)  // Near recreation
      ]
    };
  }

  public static getStressTestScenario(): DebugScenario {
    return {
      name: "Stress Test Layout",
      description: "Complex layout with multiple rooms of each type for performance testing",
      size: { width: 30, height: 20 },
      rooms: [
        // Multiple bedrooms
        { category: 'bedroom', bounds: { x: 2, y: 2, width: 3, height: 3 }, doorways: [new Point(5.5, 3)] },
        { category: 'bedroom', bounds: { x: 2, y: 6, width: 3, height: 3 }, doorways: [new Point(5.5, 7)] },
        { category: 'bedroom', bounds: { x: 2, y: 10, width: 3, height: 3 }, doorways: [new Point(5.5, 11)] },
        
        // Multiple bathrooms
        { category: 'bathroom', bounds: { x: 7, y: 2, width: 2, height: 2 }, doorways: [new Point(6.5, 3)] },
        { category: 'bathroom', bounds: { x: 7, y: 5, width: 2, height: 2 }, doorways: [new Point(6.5, 6)] },
        
        // Large dining area
        { category: 'dining', bounds: { x: 11, y: 2, width: 6, height: 4 }, doorways: [new Point(10.5, 4)] },
        
        // Medical wing
        { category: 'medical', bounds: { x: 19, y: 2, width: 5, height: 6 }, doorways: [new Point(18.5, 5)] },
        
        // Recreation areas
        { category: 'recreation', bounds: { x: 11, y: 8, width: 4, height: 4 }, doorways: [new Point(10.5, 10)] },
        { category: 'recreation', bounds: { x: 16, y: 8, width: 4, height: 4 }, doorways: [new Point(15.5, 10)] },
        
        // Main hallway
        { category: 'utility', bounds: { x: 6, y: 2, width: 2, height: 11 }, doorways: [] },
        
        // Cross hallway
        { category: 'utility', bounds: { x: 10, y: 8, width: 8, height: 2 }, doorways: [] }
      ],
      furniture: [
        // Spread furniture across rooms with strategic placement
        { type: 'bed_twin', position: new Point(3, 3), rotation: 0 },
        { type: 'bed_twin', position: new Point(3, 7), rotation: 0 },
        { type: 'bed_twin', position: new Point(3, 11), rotation: 0 },
        { type: 'dining_table', position: new Point(13, 4), rotation: 0 },
        { type: 'sofa', position: new Point(12, 10), rotation: 0 },
        { type: 'hospital_bed', position: new Point(21, 5), rotation: 0 }
      ],
      residentSpawns: [
        new Point(100, 120), // Central hallway
        new Point(140, 100), // Near dining
        new Point(180, 140), // Near medical
        new Point(120, 160), // Near recreation
        new Point(100, 180)  // Lower hallway
      ]
    };
  }

  public static getMinimalTestScenario(): DebugScenario {
    return {
      name: "Minimal Test",
      description: "Simple 2-room setup for quick testing",
      size: { width: 10, height: 8 },
      rooms: [
        {
          category: 'bedroom',
          bounds: { x: 2, y: 2, width: 3, height: 3 },
          doorways: [new Point(5.5, 3)]
        },
        {
          category: 'dining',
          bounds: { x: 6, y: 2, width: 3, height: 3 },
          doorways: [new Point(5.5, 3)]
        }
      ],
      furniture: [
        { type: 'bed_twin', position: new Point(3, 3), rotation: 0 },
        { type: 'dining_table', position: new Point(7, 3), rotation: 0 }
      ],
      residentSpawns: [
        new Point(80, 80), // Between rooms
        new Point(120, 80)
      ]
    };
  }

  private placeDoors(room: DebugRoom): void {
    if (!room.doorways || room.doorways.length === 0) return;
    
    for (const doorway of room.doorways) {
      // Determine door orientation and placement
      const bounds = room.bounds;
      let startX, startY, endX, endY;
      
      // Check which wall this doorway is on
      if (Math.abs(doorway.y - (bounds.y - 0.5)) < 0.1) {
        // Top wall - horizontal door
        startX = doorway.x - 0.5;
        startY = doorway.y;
        endX = doorway.x + 0.5;
        endY = doorway.y;
      } else if (Math.abs(doorway.y - (bounds.y + bounds.height - 0.5)) < 0.1) {
        // Bottom wall - horizontal door
        startX = doorway.x - 0.5;
        startY = doorway.y;
        endX = doorway.x + 0.5;
        endY = doorway.y;
      } else if (Math.abs(doorway.x - (bounds.x - 0.5)) < 0.1) {
        // Left wall - vertical door
        startX = doorway.x;
        startY = doorway.y - 0.5;
        endX = doorway.x;
        endY = doorway.y + 0.5;
      } else if (Math.abs(doorway.x - (bounds.x + bounds.width - 0.5)) < 0.1) {
        // Right wall - vertical door
        startX = doorway.x;
        startY = doorway.y - 0.5;
        endX = doorway.x;
        endY = doorway.y + 0.5;
      } else {
        console.warn(`⚠️ Could not determine door placement for doorway at (${doorway.x}, ${doorway.y})`);
        continue;
      }
      
      this.doorSystem.addDoor(startX, startY, endX, endY, 0);
    }
  }
}